import { VALID_TABS } from "@/constants"
import {
  addFunnel,
  emptyFilter,
  filterNames,
  printChannelFilter,
} from "@/lib/channels/channel-filter"
import {
  DIRECTORY_PARAMS,
  type DirectoryParams,
} from "@/lib/directory/directory-view"
import {
  type HistorySearch,
  validateHistorySearch,
} from "@/lib/history/history-params"
import { tabFromSearch, tabSearch } from "@/lib/workspace-tabs"
import type { TabType } from "@/types"

export type WorkspaceSearch = {
  tab?: TabType
  /** An old Settings section id or a topic id; the page lands on its topic. */
  section?: string
  /** A Setting, panel or topic id the Settings page scrolls to (SET-02). */
  setting?: string
  /**
   * The Channel filter on the Channels tab, in its text form
   * (`lib/channels/channel-filter.ts`). It replaced `channelGroup`, whose old
   * links become a one-Condition filter on that Setting group.
   */
  channelFilter?: string
  /**
   * The Post filter on the Posts tab, in its text form
   * (`lib/posts/post-filter.ts`). It decides what the tab shows, never what an
   * Artifact covers, so it is a view's address rather than a Scope's.
   */
  postFilter?: string
  /** Selected setting group in Settings → Channels & Sync. */
  settingGroup?: string
  /**
   * Saved Discover report to open on the Discover tab.
   *
   * In the URL rather than component state so History can deep-link a report,
   * and so reopening one is shareable and survives a reload. Absent means
   * "the most recent report".
   */
  report?: string
  /**
   * The other three artifact kinds, on the same terms.
   *
   * Opening an artifact from History is a navigation — it should survive a
   * reload and be worth copying out of the address bar. Only Discover reports
   * were deep-linkable before; a summary was restored through component state
   * and a chat could not be reopened at all.
   */
  summary?: string
  chatSession?: string
  tagRun?: string
} & DirectoryParams &
  HistorySearch

/** The string params: kept trimmed when non-blank, dropped otherwise. */
const ID_PARAMS = [
  "setting",
  "channelFilter",
  "postFilter",
  "settingGroup",
  "report",
  "summary",
  "chatSession",
  "tagRun",
] as const

function trimmedString(value: unknown): string | undefined {
  return typeof value === "string" ? value.trim() || undefined : undefined
}

function workspaceTab(raw: unknown): TabType {
  return VALID_TABS.includes(raw as TabType) ? (raw as TabType) : "channels"
}

/** Old Settings sections that held a tool; they live on the Tools tab now. */
const TOOL_SECTIONS = [
  "quick-message",
  "diagnostics",
  "network-telemetry",
  "table-sizes",
  "transfer",
  "query",
  "tools",
  "telemetry",
  "logs",
  // The old Data section: three of its four entries are tools now.
  "db",
  "data",
  "data-management",
]

/**
 * `validateSearch` for `/workspace`. The legacy `?tab=network` became the
 * Network section of Settings, and nothing else survives that redirect.
 */
export function validateWorkspaceSearch(
  search: Record<string, unknown>,
): WorkspaceSearch {
  if (search.tab === "network") return { tab: "settings", section: "network" }
  const result: WorkspaceSearch = { tab: workspaceTab(search.tab) }
  const section = trimmedString(search.section)
  if (result.tab === "settings" && section && TOOL_SECTIONS.includes(section))
    return { tab: "tools" }
  if (section) result.section = section
  for (const key of ID_PARAMS) {
    const value = trimmedString(search[key])
    if (value) result[key] = value
  }
  for (const key of DIRECTORY_PARAMS) {
    const raw = search[key]
    const text = typeof raw === "number" ? String(raw) : trimmedString(raw)
    // A blank `?dirFilter=` is the empty filter, which absence is not.
    if (text) result[key] = text
    else if (key === "dirFilter" && raw === "") result[key] = ""
  }
  // On every tab, as the Directory's are: the palette sets History's search
  // and Starred from anywhere, and History keeps them across a tab switch.
  Object.assign(result, validateHistorySearch(search))
  const legacyGroup = trimmedString(search.channelGroup)
  if (legacyGroup && !result.channelFilter) {
    // No group names here, so it is written by id, which reads back.
    result.channelFilter = printChannelFilter(
      addFunnel(emptyFilter(), "group", legacyGroup),
      filterNames([]),
    )
  }
  // Only the active tab's Artifact param means anything (TABS-01). An old
  // `?summary=` riding along on `?tab=posts` would otherwise reopen that
  // Summary the next time the Summary tab was focused.
  return tabSearch(result, tabFromSearch(result))
}
